import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import { Users } from "lucide-react";

import FilterInput from "../../../Filter/FilterInput";
import { UIButton, UICheckbox, UIEmptyState, UIUserInfo } from "../../../ui";
import messages from "../../../../assets/locale/messages";
import useAssignableApplicants from "./hooks/useAssignableApplicants";

const ROLE_FILTER_NAME = "applicant-role";

const AssignApplicantsPicker = ({
  eventId,
  portalVariant,
  roles,
  selectedIds,
  onSelectItems,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { zoneApplicants, shared } = messages[lang];

  const [filterRole, setFilterRole] = useState(null);
  const [isRoleFilterOpen, setIsRoleFilterOpen] = useState(false);

  // Focus the field too, so the popup opened from the empty state is keyboard
  // navigable instead of only pointer-reachable.
  const handleOpenRoleFilter = () => {
    document.getElementById(ROLE_FILTER_NAME)?.focus();
    setIsRoleFilterOpen(true);
  };

  const { items, isLoading, hasMore, loadMore } = useAssignableApplicants({
    eventId,
    portalVariant,
    roleId: filterRole?.id,
    lang,
  });

  const handleToggle = (id) => (checked) => {
    onSelectItems(
      checked
        ? [...selectedIds, id]
        : selectedIds.filter((selectedId) => selectedId !== id)
    );
  };

  const isEmpty = !isLoading && !items.length;

  return (
    <Box className="zone-applicants-picker">
      <Box className="zone-applicants-picker__toolbar">
        <Box className="zone-applicants-picker__role">
          <Typography
            component="label"
            htmlFor={ROLE_FILTER_NAME}
            className="zone-applicants-picker__role-label"
          >
            {zoneApplicants.applicantsRole}
          </Typography>
          <FilterInput
            id={ROLE_FILTER_NAME}
            name={ROLE_FILTER_NAME}
            placeholder={zoneApplicants.allRoles}
            options={roles}
            value={filterRole}
            onChange={(value) => setFilterRole(value)}
            open={isRoleFilterOpen}
            onOpen={() => setIsRoleFilterOpen(true)}
            onClose={() => setIsRoleFilterOpen(false)}
            filterWrapperClass="zone-applicants-picker__role-filter"
          />
        </Box>
        {isEmpty ? null : (
          <Typography className="zone-applicants-picker__count">
            {zoneApplicants.selectedCount.replace("{count}", selectedIds.length)}
          </Typography>
        )}
      </Box>

      {isEmpty ? (
        <UIEmptyState
          classNames={{ root: "zone-applicants-picker__empty" }}
          icon={<Users size={40} />}
          title={zoneApplicants.allAssigned.title}
          // Only a role filter is worth changing — with "All Roles" selected
          // there is nothing left to widen to.
          description={
            filterRole
              ? zoneApplicants.allAssigned.description.replace(
                  "{role}",
                  filterRole.name
                )
              : null
          }
          actionLabel={filterRole ? zoneApplicants.changeRole : null}
          onAction={filterRole ? handleOpenRoleFilter : undefined}
          actionButtonProps={{ variant: "primary", appearance: "outline" }}
        />
      ) : (
        <Box className="zone-applicants-picker__list">
          {items.map((item) => (
            <UICheckbox
              key={item.id}
              name={`applicant-${item.id}`}
              checked={selectedIds.includes(item.requestId)}
              onChange={handleToggle(item.requestId)}
              label={
                <UIUserInfo
                  name={item.name || "-"}
                  description={zoneApplicants.applicantRole.replace(
                    "{role}",
                    item.requestedRole || "-"
                  )}
                  image={item.image}
                  className="zone-applicants-picker__user"
                />
              }
            />
          ))}

          {hasMore && (
            <UIButton
              variant="text"
              className="zone-applicants-picker__more"
              onClick={loadMore}
            >
              {shared.viewMore}
            </UIButton>
          )}
        </Box>
      )}
    </Box>
  );
};

AssignApplicantsPicker.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  portalVariant: PropTypes.string,
  roles: PropTypes.array,
  selectedIds: PropTypes.array,
  onSelectItems: PropTypes.func,
};

AssignApplicantsPicker.defaultProps = {
  eventId: undefined,
  portalVariant: undefined,
  roles: [],
  selectedIds: [],
  onSelectItems: undefined,
};

export default AssignApplicantsPicker;
